import { Suspense } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'
import { RootLayout } from '../layout/RootLayout'
import ErrorPage from '@/app/pages/ErrorPage'
import LoginPage from '@/app/pages/LoginPage'
import { AuthGuard } from './AuthGuard'
import { RedirectWithSuffix } from './components/RedirectWithSuffix'
import { lazyWithRetry } from './lazyWithRetry'
import { LoadingState } from '@/components/ui/content-states'

// ルートベースのコード分割 - 動的インポート
const InventoryDashboardPage = lazyWithRetry(
  () => import('@/features/materials/pages/InventoryDashboardPage'),
  'dashboard-materials',
)
const DashboardPage = lazyWithRetry(
  () => import('@/features/dashboard/pages/DashboardPage'),
  'dashboard-home',
)
const MastersPage = lazyWithRetry(
  () => import('@/features/masters/pages/MastersPage'),
  'dashboard-masters',
)
const MaterialItemsPage = lazyWithRetry(
  () => import('@/features/masters/pages/MaterialItemsPage'),
  'dashboard-material-items',
)
const CompanyVehiclesPage = lazyWithRetry(
  () => import('@/features/masters/pages/CompanyVehiclesPage'),
  'dashboard-company-vehicles',
)
const CraftsmenPage = lazyWithRetry(
  () => import('@/features/masters/pages/CraftsmenPage'),
  'dashboard-craftsmen',
)
const TruckDriversPage = lazyWithRetry(
  () => import('@/features/masters/pages/TruckDriversPage'),
  'dashboard-truck-drivers',
)
const TrucksPage = lazyWithRetry(
  () => import('@/features/masters/pages/TrucksPage'),
  'dashboard-trucks',
)
const NotFoundPage = lazyWithRetry(
  () => import('@/app/pages/NotFoundPage'),
  'not-found',
)
const ProjectDetailClient = lazyWithRetry(
  () => import('@/features/projects/components/ProjectDetailClient'),
  'dashboard-project-detail',
)
const ProjectsPage = lazyWithRetry(
  () => import('@/features/projects/pages/ProjectsPage'),
  'dashboard-projects',
)
const UsersPage = lazyWithRetry(
  () => import('@/features/users/pages/UsersPage'),
  'dashboard-users',
)
const MonthlyReportPage = lazyWithRetry(
  () => import('@/features/projects/components/MonthlyReport/MonthlyReportPage'),
  'dashboard-reports',
)
const LandingRoute = lazyWithRetry(
  () => import('@/features/landing/LandingRoute'),
  'landing',
)

// ローディングラッパー
const LazyRoute = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<div className="flex-1 flex items-center justify-center"><LoadingState /></div>}>
    {children}
  </Suspense>
)

export const router = createBrowserRouter([
  {
    path: '/',
    element: <LazyRoute><LandingRoute /></LazyRoute>,
    errorElement: <ErrorPage />
  },
  {
    path: '/login',
    element: <LoginPage />,
    errorElement: <ErrorPage />
  },
  // 旧 /dashboard/* を新トップレベルへ恒久リダイレクト（既存クライアントのブックマーク対策）。
  // /dashboard 単体はホーム（進行中案件）として下のレイアウトルートで処理するため、ここでは扱わない。
  {
    path: '/dashboard/projects',
    element: <RedirectWithSuffix fromBase="/dashboard/projects" toBase="/projects" />
  },
  {
    path: '/dashboard/projects/*',
    element: <RedirectWithSuffix fromBase="/dashboard/projects" toBase="/projects" />
  },
  {
    path: '/dashboard/project-detail',
    element: <RedirectWithSuffix fromBase="/dashboard/project-detail" toBase="/project-detail" />
  },
  {
    path: '/dashboard/masters',
    element: <RedirectWithSuffix fromBase="/dashboard/masters" toBase="/masters" />
  },
  {
    path: '/dashboard/masters/*',
    element: <RedirectWithSuffix fromBase="/dashboard/masters" toBase="/masters" />
  },
  {
    path: '/dashboard/users',
    element: <RedirectWithSuffix fromBase="/dashboard/users" toBase="/users" />
  },
  {
    path: '/dashboard/users/*',
    element: <RedirectWithSuffix fromBase="/dashboard/users" toBase="/users" />
  },
  {
    path: '/dashboard/materials',
    element: <RedirectWithSuffix fromBase="/dashboard/materials" toBase="/materials" />
  },
  {
    path: '/dashboard/materials/*',
    element: <RedirectWithSuffix fromBase="/dashboard/materials" toBase="/materials" />
  },
  {
    path: '/dashboard/reports',
    element: <RedirectWithSuffix fromBase="/dashboard/reports" toBase="/reports" />
  },
  {
    path: '/dashboard/reports/*',
    element: <RedirectWithSuffix fromBase="/dashboard/reports" toBase="/reports" />
  },
  // 認証エリア。pathless レイアウトルートで各トップレベルパスを包む。
  // 子は相対パスのため /projects /masters /materials /users /reports に解決される。
  // /dashboard はホーム（進行中案件）として据え置き。
  {
    element: <RootLayout />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: 'dashboard',
        element: <LazyRoute><AuthGuard><DashboardPage /></AuthGuard></LazyRoute>
      },
      {
        path: 'projects',
        children: [
          {
            index: true,
            element: <LazyRoute><AuthGuard><ProjectsPage /></AuthGuard></LazyRoute>
          },
          {
            path: ':project_id',
            element: <LazyRoute><AuthGuard><ProjectDetailClient /></AuthGuard></LazyRoute>
          }
        ]
      },
      {
        path: 'project-detail',
        element: <LazyRoute><AuthGuard><ProjectDetailClient /></AuthGuard></LazyRoute>
      },
      {
        path: 'masters',
        children: [
          {
            index: true,
            element: <LazyRoute><AuthGuard><MastersPage /></AuthGuard></LazyRoute>
          },
          {
            path: 'material-items',
            element: <LazyRoute><AuthGuard><MaterialItemsPage /></AuthGuard></LazyRoute>
          },
          {
            path: 'company-vehicles',
            element: <LazyRoute><AuthGuard><CompanyVehiclesPage /></AuthGuard></LazyRoute>
          },
          {
            path: 'craftsmen',
            element: <LazyRoute><AuthGuard><CraftsmenPage /></AuthGuard></LazyRoute>
          },
          {
            path: 'truck-drivers',
            element: <LazyRoute><AuthGuard><TruckDriversPage /></AuthGuard></LazyRoute>
          },
          {
            path: 'trucks',
            element: <LazyRoute><AuthGuard><TrucksPage /></AuthGuard></LazyRoute>
          }
        ]
      },
      {
        path: 'users',
        element: <LazyRoute><AuthGuard><UsersPage /></AuthGuard></LazyRoute>
      },
      {
        path: 'materials',
        children: [
          {
            index: true,
            element: <LazyRoute><AuthGuard><InventoryDashboardPage /></AuthGuard></LazyRoute>
          },
          {
            path: 'receipt',
            element: <Navigate to="/materials?sheet=receipt&mode=create" replace />
          },
          {
            path: 'site-usage',
            element: <Navigate to="/materials?sheet=usage&mode=create" replace />
          },
          {
            path: 'summary',
            element: <Navigate to="/materials?sheet=summary" replace />
          }
        ]
      },
      {
        path: 'reports',
        element: <LazyRoute><AuthGuard><MonthlyReportPage /></AuthGuard></LazyRoute>
      },
      {
        path: '*',
        element: <LazyRoute><NotFoundPage /></LazyRoute>
      }
    ]
  }
])
